/**
 * ドリルダウン階層マスタ(hierarchy_nodes)の読み書き。
 *
 * 階層の深さ・名称は事業部ごとに自由に定義する(要件定義書 4.4)。
 * 取引は末端ノードに紐づけるのが基本だが、中間ノードへの紐づけも許可する。
 *
 * 編集できるのは管理者のみ(decisions.md 2026-08-20)。
 */
import {
  collection,
  deleteDoc,
  doc,
  getDocs,
  limit,
  query,
  setDoc,
  updateDoc,
  where,
  type QueryDocumentSnapshot,
} from 'firebase/firestore';
import { getDb } from '@core/lib/firebase';
import type { DepartmentId, HierarchyNode } from '@core/lib/types';

const COLLECTION = 'hierarchy_nodes';

function withId(snap: QueryDocumentSnapshot): HierarchyNode {
  return { id: snap.id, ...(snap.data() as object) } as HierarchyNode;
}

/**
 * 事業部の階層ノードをすべて取得する。
 * 並べ替えはクライアント側(事業部あたり数十件程度のため、複合インデックスを増やさない)。
 */
export async function fetchHierarchyNodes(
  departmentId: DepartmentId,
  includeInactive = false,
): Promise<HierarchyNode[]> {
  const snap = await getDocs(
    query(collection(getDb(), COLLECTION), where('department_id', '==', departmentId)),
  );
  return snap.docs
    .map(withId)
    .filter((n) => includeInactive || n.active)
    .sort((a, b) => a.display_order - b.display_order || a.name.localeCompare(b.name, 'ja'));
}

/** 直下の子ノード */
export function childrenOf(nodes: HierarchyNode[], parentId: string | null): HierarchyNode[] {
  return nodes.filter((n) => (n.parent_id ?? null) === parentId);
}

/** ツリーを「拠点 › 事業所 › コース」のようなパス表記にする(選択肢の表示用) */
export function toPathLabel(node: HierarchyNode, all: HierarchyNode[]): string {
  const byId = new Map(all.map((n) => [n.id, n]));
  const names: string[] = [node.name];
  let current = node.parent_id ? byId.get(node.parent_id) : undefined;
  while (current) {
    names.unshift(current.name);
    current = current.parent_id ? byId.get(current.parent_id) : undefined;
  }
  return names.join(' › ');
}

export type HierarchyNodeInput = {
  departmentId: DepartmentId;
  parentId: string | null;
  name: string;
  levelLabel: string;
  displayOrder: number;
  active: boolean;
};

export async function createHierarchyNode(
  input: HierarchyNodeInput,
  all: HierarchyNode[],
): Promise<void> {
  if (!input.name.trim()) throw new Error('名称を入力してください');
  const parent = input.parentId ? all.find((n) => n.id === input.parentId) : null;
  const id = doc(collection(getDb(), COLLECTION)).id;

  await setDoc(doc(getDb(), COLLECTION, id), {
    department_id: input.departmentId,
    parent_id: input.parentId,
    // path は祖先IDの配列。部分木の集計に使う
    path: parent ? [...parent.path, parent.id] : [],
    name: input.name.trim(),
    level_label: input.levelLabel.trim(),
    display_order: input.displayOrder,
    active: input.active,
  });
}

/** 名称・階層名・表示順・有効フラグの変更。親の付け替えはフェーズ3以降(path再計算が必要) */
export async function updateHierarchyNode(
  id: string,
  patch: Pick<HierarchyNodeInput, 'name' | 'levelLabel' | 'displayOrder' | 'active'>,
): Promise<void> {
  if (!patch.name.trim()) throw new Error('名称を入力してください');
  await updateDoc(doc(getDb(), COLLECTION, id), {
    name: patch.name.trim(),
    level_label: patch.levelLabel.trim(),
    display_order: patch.displayOrder,
    active: patch.active,
  });
}

/** この階層に紐づく取引があるか(削除前の確認に使う) */
export async function hasTransactionsOn(nodeId: string): Promise<boolean> {
  const snap = await getDocs(
    query(collection(getDb(), 'transactions'), where('hierarchy_node_id', '==', nodeId), limit(1)),
  );
  return !snap.empty;
}

/**
 * 削除。子ノードがある場合と、取引が紐づいている場合は削除しない
 * (数字の根拠が辿れなくなるため。使わなくなったノードは「無効」にする)
 */
export async function deleteHierarchyNode(id: string, all: HierarchyNode[]): Promise<void> {
  if (childrenOf(all, id).length > 0) {
    throw new Error('下位の階層があるため削除できません。先に下位を削除するか、無効にしてください');
  }
  if (await hasTransactionsOn(id)) {
    throw new Error('この階層に紐づく取引があるため削除できません。「無効」にしてください');
  }
  await deleteDoc(doc(getDb(), COLLECTION, id));
}
